/**
 * PricingPage - Full pricing page
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore, useSubscriptionStore } from '@/stores';
import { PricingTable } from '../components/PricingTable';
import type { SubscriptionTier } from '@/types/subscription';

export function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const { createCheckoutSession, isLoading } = useSubscriptionStore();

  const canceled = searchParams.get('canceled') === 'true';

  const handleSelectTier = async (tier: SubscriptionTier, billingPeriod: 'monthly' | 'annual') => {
    if (tier === 'free') {
      navigate('/');
      return;
    }

    if (!isAuthenticated) {
      navigate('/signup', { state: { selectedTier: tier } });
      return;
    }

    // Map tier + billing period to Stripe price ID
    const priceIds: Record<string, Record<'monthly' | 'annual', string>> = {
      pro: {
        monthly: import.meta.env.VITE_STRIPE_PRO_PRICE_ID || 'price_pro_monthly',
        annual: import.meta.env.VITE_STRIPE_PRO_ANNUAL_PRICE_ID || 'price_pro_annual',
      },
      enterprise: {
        monthly: import.meta.env.VITE_STRIPE_ENTERPRISE_PRICE_ID || 'price_enterprise_monthly',
        annual: import.meta.env.VITE_STRIPE_ENTERPRISE_ANNUAL_PRICE_ID || 'price_enterprise_annual',
      },
    };

    const url = await createCheckoutSession(priceIds[tier][billingPeriod]);
    if (url) {
      window.location.href = url;
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-[#0a0f1a] via-slate-900 to-[#0a0f1a]">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/3 w-[500px] h-[500px] bg-hud-primary/[0.04] rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-teal-500/[0.03] rounded-full blur-[100px]" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-4 py-4 sm:px-6 sm:py-5">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-text-secondary hover:text-white transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>

        {!isAuthenticated && (
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-hud-primary hover:text-hud-primary/80 transition-colors"
          >
            Sign In
          </button>
        )}
      </nav>

      {/* Content */}
      <div className="relative z-10 px-4 sm:px-6 py-6 sm:py-12 pb-16">
        {/* Canceled notice */}
        {canceled && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto mb-6 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-center"
          >
            <p className="text-yellow-400 text-sm">
              Checkout was canceled. Feel free to try again when you're ready.
            </p>
          </motion.div>
        )}

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-4xl font-bold text-white mb-2 sm:mb-4"
          >
            Choose Your Plan
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-sm sm:text-base text-text-secondary max-w-xl mx-auto"
          >
            AI-powered air quality insights, smart route planning, and advanced health monitoring.
          </motion.p>
        </div>

        {/* Loading overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-slate-800/90 backdrop-blur-xl border border-white/10">
              <svg className="animate-spin w-8 h-8 text-hud-primary" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-text-secondary text-sm">Preparing checkout...</p>
            </div>
          </div>
        )}

        {/* Pricing Table */}
        <PricingTable onSelectTier={handleSelectTier} />
      </div>
    </div>
  );
}
